import GenericForm, { IFormRows } from 'components/forms/GenericForm';
import moment from 'moment';
import { useCreateActualStock } from 'pages/admin/capacity/currentStock/hooks/useCreateActualStock';
import { useEditActualStock } from 'pages/admin/capacity/currentStock/hooks/useEditActualStock';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { renderFamily, renderProduct } from 'utils/forms/autocomplete';
import { makeGenericFormInputStructure } from 'utils/forms/defaultInputs';
import {
	defaultFormStyle,
	defaultInputStyle,
	formFlexProps,
	rowFlexProps,
} from 'utils/forms/defaultsProps';
import { validateRequired } from 'utils/forms/validations';
import { currencyToNumber } from 'utils/functions/currencyToNumber';

import { useProductFamilyLogic } from '../../../../hooks/useProductFamilyLogic';

interface IFormCadastrarEstoqueAtual {
	initialState: any;
	actualStockId?: number;
	autocompleteAdditionalData?: any;
}

const FormCadastrarEditarEstoqueAtual = ({
	initialState,
	actualStockId = 0,
	autocompleteAdditionalData,
}: IFormCadastrarEstoqueAtual) => {
	const history = useHistory();

	const isEditing = !!actualStockId;

	const [formProps, setFormProps] = useState<any>();
	const [forceFamilyValue, setForceFamilyValue] = useState<any>(null);

	const {
		familyId,
		selectedProduct,
		handleOnChangeFamily,
		handleOnChangeProduct,
		isDisabledProductAutocomplete,
		shouldClearAutocompleteProduct,
	} = useProductFamilyLogic({
		initialFamilyId: initialState?.familia_id,
		initialProduct: autocompleteAdditionalData?.product,
		onFamilyAutoFill: (familyId, familyName) => {
			if (formProps) {
				const { setValue } = formProps;
				setValue('familia_id', familyId);
			}
		},
	});

	const [payload, setPayload] = useState<any>();

	const { mutate: createActualStock } = useCreateActualStock(payload);
	const { mutate: updateActualStock } = useEditActualStock(
		payload,
		actualStockId
	);

	const goToEstoqueAtual = useCallback(() => history.goBack(), [history]);

	const handleOnChangeProductWithForm = useCallback(
		(value, formProps) => {
			if (!formProps) setFormProps(formProps);
			handleOnChangeProduct(value);

			if (value?.familia_id && value?.nome_familia) {
				const familyData = {
					id: value.familia_id,
					nome_familia: value.nome_familia,
					empresa_id: value.empresa_id,
				};

				if (formProps) {
					const { setValue } = formProps;
					setValue('familia_id', value.familia_id);
				}

				setForceFamilyValue(familyData);
				handleOnChangeFamily(familyData);

				setTimeout(() => {
					setForceFamilyValue(null);
				}, 100);
			}
		},
		[handleOnChangeProduct, handleOnChangeFamily]
	);

	const getMaxDate = useMemo(
		() => moment().startOf('day').format('YYYY-MM-DD'),
		[]
	);

	const onSubmit = useCallback(
		values => {
			const body = {
				data: moment(values?.data, 'YYYY-MM-DD').toISOString() || null,
				familia_id: values?.familia_id || null,
				produto_id: values?.produto_id || null,
				quantidade: values?.quantidade
					? currencyToNumber(values?.quantidade)
					: null,
			};

			setPayload(body);

			if (isEditing) updateActualStock();
			else createActualStock();
		},
		[createActualStock, isEditing, updateActualStock]
	);

	const renderRows = useCallback((): IFormRows[] => {
		return [
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'date',
							name: 'data',
							label: 'Data',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							inputProps: {
								'data-test':
									'input-data-cadastrar_editar-page-estoque_atual',
								...defaultInputStyle,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'familia_id',
							label: 'Família',
							showRequiredOnLabel: false,
							inputProps: {
								'data-test':
									'autocomplete-familia-cadastrar_editar-page-estoque_atual',
								...defaultInputStyle,
								searchType: 'familia',
								renderInputLabel: renderFamily,
								autocomplete: 'off',
								initialLabel: autocompleteAdditionalData?.family
									? renderFamily(
											autocompleteAdditionalData?.family
									  )
									: '',
								onChange: handleOnChangeFamily,
								forceValue: forceFamilyValue,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'produto_id',
							label: 'Produto',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							disabled: isDisabledProductAutocomplete,
							inputProps: {
								'data-test':
									'autocomplete-produto-cadastrar_editar-page-estoque_atual',
								...defaultInputStyle,
								searchType: 'produto',
								renderInputLabel: renderProduct,
								autocomplete: 'off',
								initialLabel:
									autocompleteAdditionalData?.product
										? renderProduct(
												autocompleteAdditionalData?.product
										  )
										: '',
								extraRequestData: {
									familia_id: familyId,
								},
								clearInput: shouldClearAutocompleteProduct,
								onChange: handleOnChangeProductWithForm,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'product-quantity',
							name: 'quantidade',
							label: 'Estoque',
							showRequiredOnLabel: true,
							validate: {
								...validateRequired(),
							},
							inputProps: {
								'data-test':
									'input-estoque-cadastrar_editar-page-estoque_atual',
								...defaultInputStyle,
							},
						}),
					},
				],
			},
		];
	}, [
		familyId,
		getMaxDate,
		handleOnChangeFamily,
		handleOnChangeProductWithForm,
		autocompleteAdditionalData,
		isDisabledProductAutocomplete,
		shouldClearAutocompleteProduct,
		forceFamilyValue,
	]);

	const renderForm = useCallback(() => {
		return (
			<>
				<GenericForm
					dataTestProps={{
						cancel: 'button-cancelar-page-estoque_atual',
						save: 'button-salvar-page-estoque_atual',
					}}
					initialState={initialState}
					formFlexProps={formFlexProps}
					rows={renderRows()}
					style={defaultFormStyle}
					onSubmit={onSubmit}
					onCancel={goToEstoqueAtual}
				/>
			</>
		);
	}, [initialState, onSubmit, renderRows, goToEstoqueAtual]);

	return renderForm();
};

export default FormCadastrarEditarEstoqueAtual;
